import { useDateRange } from '../../../../../store/zustand/dateRangeStore'
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { DateRange } from '../../../../../lib/types/DateRange'
import { Link, useSearchParams } from 'react-router-dom'
import { useDownloadPDFReport } from '../../../../../api/dealership/reports/useDownloadPDFReport'
import PageHeader from '../../../../../components/PageHeader/PageHeader'
import {
  Button,
  StatsCard,
  Table,
  MonthPicker,
  Toast,
  SelectInput,
} from '@demandlocal/components'
import toast from 'react-hot-toast'
import mtm from '../../../../../lib/utils/mtm'
import { GA4 } from '../../../../../lib/utils/ga4'
import { ReactComponent as DownloadIcon } from '../../../../../images/icons/action_download.svg'
import { useDealership } from '../../../../../store/zustand/dealershipStore'
import { createColumnHelper } from '@tanstack/react-table'
import { Campaign } from '../../../../../lib/types/Campaign'
import dayjs from 'dayjs'
import { useGetAgencyByAgencyId } from '../../../../../api/agencies/useGetAgencyByAgencyId'
import { formatCurrency, formatNumber } from '../../../../../lib/utils/formatNumber'
import { useGetCampaignsSummaryList } from '../../../../../api/dealership/campaigns/useGetCampaignsSummaryList'
import { useTranslation } from 'react-i18next'
import KPICard from '../../../../../components/Overview/KPICard'
import { ReactComponent as LeadsIcon } from '../../../../../images/icons/email_icon.svg'
import { ReactComponent as ClickToCallIcon } from '../../../../../images/icons/phone_icon.svg'
import { ReactComponent as ClickToChatIcon } from '../../../../../images/icons/click_to_chat_icon.svg'
import { useGetCampaignsSummaryReport } from '../../../../../api/dealership/campaigns/reports/useGetCampaignsSummaryReport'
import { useGetTotalBudget } from '../../../../../api/dealership/profile/useGetTotalBudget'
import { useGetConversionsSummary } from '../../../../../api/dealership/performance/conversions/useGetConversionsSummary'
import { useGetConversionPathsTrackingTags } from '../../../../../api/dealership/performance/conversionPaths/useGetConversionPathsTrackingTags'
import { useGetMonthlyImpressions } from '../../../../../api/dealership/performance/useGetMonthlyImpressions'
import LineChart from '../../../../../components/Charts/LineChart'
import ComboChart from '../../../../../components/Charts/ComboChart'
import { useGetVendorCampaignsList } from '../../../../../api/dealership/vendor-campaigns/useGetVendorCampaignsList'
import { isEmpty, isEqual } from 'lodash'
import { FormProvider, useForm } from 'react-hook-form'
import { useGetBasicCampaignsList } from '../../../../../api/dealership/campaigns/useGetBasicCampaignsList'
import { useGetTagCodes } from '../../../../../api/utils/useGetTagCodes'
import StackedBarChartV2 from '../../../../../components/Charts/StackedBarChartV2'

const columnHelper = createColumnHelper<Campaign>()

type FormValues = {
  campaign_ids: Array<{ label: string; value: string }>
  dateRange: DateRange
}

const PerformanceConversionPathsFilters = ({
  setFilters,
}: {
  setFilters: React.Dispatch<React.SetStateAction<Record<string, unknown>>>
}): JSX.Element => {
  const { data: campaigns } = useGetBasicCampaignsList({})
  const campaignOptions = useMemo(() => {
    if (!campaigns) return []
    return campaigns?.data?.map((tag) => ({
      label: tag.client_campaign_display_name,
      value: tag.client_campaign_id,
    }))
  }, [campaigns])

  const [searchParams, setSearchParams] = useSearchParams()
  const { dateRange, setDateRange } = useDateRange()

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  const formContext = useForm<FormValues>({
    defaultValues: () => {
      return new Promise((resolve) => {
        resolve({
          campaign_ids: [],
          dateRange,
        })
      })
    },
  })

  const { watch, setValue } = formContext
  const campaign_ids = watch('campaign_ids')

  useEffect(() => {
    if (isEmpty(campaign_ids) && !isEmpty(campaignOptions)) {
      const selectedCampaignIds = searchParams.getAll('campaign_ids')
      const newCampaignIds = campaignOptions.filter((option) =>
        selectedCampaignIds.includes(option.value.toString()),
      )

      if (!isEmpty(selectedCampaignIds) && !isEqual(campaign_ids, newCampaignIds))
        setValue('campaign_ids', newCampaignIds)
    }
  }, [setValue, searchParams, campaignOptions, campaign_ids])

  return (
    <FormProvider {...formContext}>
      <form className={'print:hidden'}>
        <div
          className={
            'p-5 relative rounded-md border-2 bg-primary-persian-blue/5 border-primary-persian-blue/10 mb-8'
          }>
          <div className={'grid grid-cols-3 gap-5 items-center'}>
            <SelectInput
              isMulti={true}
              name={'campaign_ids'}
              onChange={(value) => {
                setFilters((prev) => ({
                  ...prev,
                  campaign_ids: value.map((v) => v.value),
                }))
                setSearchParams((sp) => {
                  if (sp.has('campaign_ids')) sp.delete('campaign_ids')
                  value.forEach((v) => sp.append('campaign_ids', v.value))
                  return sp
                })
              }}
              label={'Campaigns'}
              options={campaignOptions}
              placeholder="All Campaigns"
            />
            <div className="flex flex-col gap-2">
              <span className="font-bold text-sm">Month</span>

              <MonthPicker
                maxDate={dayjs().toDate()}
                onChange={(date) => {
                  const dateRange = {
                    dateRange: {
                      startDate: dayjs(date).startOf('month').format('YYYY-MM-DD'),
                      endDate: dayjs(date).endOf('month').format('YYYY-MM-DD'),
                    },
                  }
                  onDateRangeChange(dateRange)
                }}
              />
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

const AudienceTemplate = ({
  children,
  setFilters,
  isLoading,
}: PropsWithChildren<{
  filters: DateRange
  setFilters: React.Dispatch<React.SetStateAction<any>>
  isLoading: boolean
}>): JSX.Element => {
  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport()
  const dealership = useDealership()
  const [isExpanded, setIsExpanded] = useState(true)

  useEffect(() => {
    if (!isLoading) {
      setTimeout(() => {
        const content = document.getElementById('content')
        if (content) content.className = `${content.className} loaded`
      }, 2000)
    }
  }, [isLoading])

  return (
    <div className={'flex flex-1 flex-col h-full gap-3'} id={'content'}>
      <PageHeader
        title={`${dealership?.dealership} - Conversion Attribution`}
        rightSlot={() => (
          <div className={'flex flex-row gap-2 items-center print:hidden'}>
            <Button
              variant={'tertiary'}
              onClick={(e) => {
                e.preventDefault()
                setIsExpanded((prev) => !prev)
              }}>
              Filters
            </Button>
            <Button
              disabled={isDownloadingReport}
              leftSlot={<DownloadIcon className={'fill-white'} />}
              animate={false}
              onClick={async (e) => {
                e.preventDefault()
                const toastId = toast.loading('Generating report...')
                GA4.sendEvent({
                  event_name: 'export_pdf',
                  event_params: {
                    campaign_type: 'conversions',
                  },
                })
                mtm.sendEvent('Export', 'PDF', 'conversions')
                await downloadReport(undefined, {
                  async onSuccess(data) {
                    toast.success('Report generated successfully', { id: toastId })
                    const blob = await data.blob()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `${dealership?.dealership}_conversions_insights.pdf`
                    document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                    a.click()
                    a.remove()
                  },
                })
              }}>
              Export as PDF
            </Button>
          </div>
        )}
      />
      <div className={'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full'}>
        {isExpanded && <PerformanceConversionPathsFilters setFilters={setFilters} />}

        {children}
      </div>
    </div>
  )
}

const OmniConversionsReport = () => {
  const dealership = useDealership()
  const [queryParams, setQueryParams] = useSearchParams()
  const { dateRange } = useDateRange()
  const { data: agency } = useGetAgencyByAgencyId(dealership?.organization_id)
  const { t } = useTranslation('routes')
  const [filters, setFilters] = useState({
    campaign_ids: queryParams.getAll('campaign_ids'),
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })

  useEffect(() => {
    setQueryParams({ ...filters.dateRange, campaign_ids: filters.campaign_ids })
  }, [filters, setQueryParams])

  const campaignFilters = useMemo(
    () => filters.campaign_ids.map((c) => ({ value: c })),
    [filters.campaign_ids],
  )

  const {
    data: { conversions } = { conversions: [] },
    data: { metrics } = { metrics: [] },
    data: { uniqueSiteVisitors } = { uniqueSiteVisitors: [] },
    isLoading: isLoadingReport,
  } = useGetCampaignsSummaryReport({
    filters: {
      ...filters.dateRange,
      excludeProductCode: 'MOB',
      campaign_ids: campaignFilters,
    },
  })

  const { data: totalBudget = { data: 0, title: '' }, isLoading: isLoadingTotalBudget } =
    useGetTotalBudget({
      filters: {
        start_date: filters.dateRange.startDate,
        end_date: filters.dateRange.endDate,
      },
    })

  const { data: monthlyConversions, isLoading: isLoadingMonthlyConversions } =
    useGetConversionsSummary({
      filters: {
        startDate: dayjs().startOf('year').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        campaign_ids: campaignFilters,
      },
    })

  const { data: monthlyImpressions, isLoading: isLoadingMonthlyImpressions } =
    useGetMonthlyImpressions({
      filters: {
        startDate: dayjs().startOf('year').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        campaign_ids: campaignFilters,
      },
    })

  const { data: trackingTags, isLoading: isLoadingTrackingTags } =
    useGetConversionPathsTrackingTags()

  const {
    data: campaigns,
    isLoading: isLoadingCampaigns,
    isPreviousData: isPreviousCampaignsData,
  } = useGetCampaignsSummaryList({
    filters: {
      startDate: filters.dateRange.startDate,
      endDate: filters.dateRange.endDate,
      excludeProductCode: 'MOB',
      campaign_ids: campaignFilters,
    },
  })

  const {
    data: vendorCampaigns,
    isLoading: isLoadingVendorCampaigns,
    isPreviousData: isPreviousVendorCampaignsData,
  } = useGetVendorCampaignsList({
    filters: {
      startDate: filters.dateRange.startDate,
      endDate: filters.dateRange.endDate,
      excludeProductCode: 'MOB',
      campaign_ids: campaignFilters,
    },
  })

  const categories = useMemo(
    () => ['Audience Predictor', 'Retargeting', 'Honda Liveramp', 'Polk'],
    [],
  )

  const { data: tagCodes } = useGetTagCodes({ pageSize: 1000 })

  const initializeMetrics = useCallback(
    () => ({
      impressions: 0,
      clicks: 0,
      views: 0,
      ...tagCodes?.data.reduce((acc, t) => {
        acc[t.tag_name] = 0
        return acc
      }, {}),
    }),
    [tagCodes?.data],
  )

  const vendorCampaignConversions = useMemo(() => {
    return vendorCampaigns
      ?.reduce((acc: Array<Record<string, string | number>>, item) => {
        const category = categories.find((category) =>
          item.vendor_campaign_name.includes(category),
        )

        if (category) {
          let categoryData = acc.find(
            (entry) =>
              entry.category === category &&
              entry.client_campaign_id === item.client_campaign_id,
          )

          if (!categoryData) {
            categoryData = {
              category,
              client_campaign_id: item.client_campaign_id,
              client_campaign_display_name: item.client_campaign_display_name,
              ...initializeMetrics(),
            }
            acc.push(categoryData)
          }

          Object.keys(initializeMetrics()).forEach((metric) => {
            categoryData[metric] =
              (categoryData[metric] as number) + (Number(item[metric]) ?? 0)
          })
        }

        return acc
      }, [] as Array<Record<string, string | number>>)
      ?.map((d) => {
        return {
          ...d,
          campaign: d.client_campaign_display_name,
          category:
            d.category === 'Audience Predictor' ? 'Behavioral Targeting' : d.category,
          Impressions: d.impressions,
          'Find a Dealer Conversion Rate': d.impressions
            ? Math.round((10000 * Number(d['Find a Dealer'])) / Number(d.impressions)) /
              100
            : 0,
        }
      })
  }, [categories, initializeMetrics, vendorCampaigns])

  const totalImpressions = useMemo(() => {
    return metrics?.reduce((acc, cur) => acc + cur.impressions, 0)
  }, [metrics])

  const totalSiteVisitors = useMemo(() => {
    return uniqueSiteVisitors?.reduce((acc, cur) => acc + cur.last_campaign_uniques, 0)
  }, [uniqueSiteVisitors])

  const defaultConversions = useMemo(() => {
    return {
      find_dealer: conversions?.find((d) => d.id === 9),
      service_lead: conversions?.find((d) => d.id === 10),
      request_quote: conversions?.find((d) => d.id === 12),
      click_to_call: conversions?.find((d) => d.id === 11),
    }
  }, [conversions])

  const campaignBudgetChartData = useMemo(() => {
    const find_dealer_tag = defaultConversions?.find_dealer?.tag_name ?? 'Find a Dealer'
    return campaigns?.data?.map((c) => ({
      campaign_name: c.client_campaign_display_name,
      [find_dealer_tag]: c[find_dealer_tag],
      impressions: c.impressions,
      Budget: c.budget,
      'Find a Dealer Rate':
        c.impressions > 0
          ? Math.round((10000 * (c[find_dealer_tag] ?? 0)) / c.impressions) / 100
          : 0,
    }))
  }, [campaigns?.data, defaultConversions?.find_dealer?.tag_name])

  const chartData = useMemo(() => {
    const conversions = monthlyConversions?.data?.filter(
      (d) => d.id === defaultConversions.find_dealer?.id,
    )
    return monthlyImpressions?.data.map((impression) => {
      const conversion = conversions?.find((conv) => conv.date === impression.month)
      return {
        date: impression.month,
        impressions: impression.impressions,
        conversions: conversion ? conversion.conversions : 0,
        'Find a Dealer Conversion Rate':
          Math.round(
            (10000 * (conversion ? conversion.conversions : 0)) / impression.impressions,
          ) / 100,
      }
    })
  }, [
    defaultConversions.find_dealer?.id,
    monthlyConversions?.data,
    monthlyImpressions?.data,
  ])

  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor('start_date', {
        header: 'Booking Month',
        cell: (info) => (
          <Link
            className={'text-primary-persian-blue font-bold'}
            to={t('dealership.campaigns.report', {
              dealership,
              campaign: {
                product_code: info.row.original.product_code,
                client_campaign_id: info.row.original.client_campaign_id,
                start_date: dateRange.dateRange.startDate,
                end_date: dateRange.dateRange.endDate,
              },
            })}>
            {dayjs(info.getValue()).format("MMMM 'YY") || '-'}
          </Link>
        ),
        footer: ({ table }) =>
          `Total for ${table.getFilteredRowModel().rows.length} campaigns`,
      }),
      columnHelper.accessor('client_campaign_display_name', {
        header: 'Campaign',
        cell: (info) => info.getValue(),
      }),
      ...(Boolean(agency?.show_budget_type)
        ? [
            columnHelper.display({
              header: 'Budget',
              id: 'budget',
              cell: ({ row }) =>
                agency?.show_budget_type === 1
                  ? formatCurrency(row.original.demand_local_net)
                  : formatCurrency(row.original.budget),
            }),
          ]
        : []),
    ]
    if (defaultConversions) {
      baseColumns.push(
        ...Object.values(defaultConversions)
          .filter(
            (
              d,
            ): d is {
              id: number
              conversions: number
              tag_name: string
              tag_code: string
            } => !!d,
          )
          .map((conversion) => {
            return columnHelper.display({
              id: conversion?.tag_name,
              header: conversion?.tag_name,
              cell: (info) => formatNumber(info.row.original[conversion?.tag_name] ?? 0),
              footer: ({ table }) =>
                formatNumber(
                  table
                    .getFilteredRowModel()
                    .rows.reduce(
                      (total, row) =>
                        total + (Number(row.original[conversion.tag_name]) || 0),
                      0,
                    ),
                ),
            })
          }),
      )
    }

    return baseColumns
  }, [
    agency?.show_budget_type,
    dateRange.dateRange.endDate,
    dateRange.dateRange.startDate,
    dealership,
    defaultConversions,
    t,
  ])

  const isLoading = useMemo(
    () =>
      isLoadingReport ||
      isLoadingTotalBudget ||
      isLoadingMonthlyConversions ||
      isLoadingTrackingTags ||
      isLoadingCampaigns ||
      isLoadingMonthlyConversions ||
      isLoadingMonthlyImpressions ||
      isLoadingVendorCampaigns,
    [
      isLoadingCampaigns,
      isLoadingMonthlyConversions,
      isLoadingMonthlyImpressions,
      isLoadingReport,
      isLoadingTotalBudget,
      isLoadingTrackingTags,
      isLoadingVendorCampaigns,
    ],
  )

  return (
    <AudienceTemplate filters={filters} setFilters={setFilters} isLoading={isLoading}>
      <div className="py-5 flex flex-col gap-6 w-full font-Spartan">
        <div className={`grid grid-cols-${totalBudget?.title?.length > 0 ? 3 : 2} gap-5`}>
          {totalBudget?.title?.length > 0 && (
            <KPICard
              title={'Total Client Spend'}
              color={'blue'}
              content={
                <span className="font-bold text-xl">
                  {formatCurrency(totalBudget?.data || 0)}
                </span>
              }
              isLoading={isLoadingReport}
            />
          )}
          <KPICard
            title={'Total Impressions'}
            color={'green'}
            content={
              <span className="font-bold text-xl">
                {formatNumber(totalImpressions || 0)}
              </span>
            }
            isLoading={isLoadingReport}
          />
          <KPICard
            title={'Total Find a Dealer Conversions'}
            color={'orange'}
            content={
              <span className="font-bold text-xl">
                {formatNumber(defaultConversions.find_dealer?.conversions || 0)}
              </span>
            }
            isLoading={isLoadingReport}
          />
        </div>
        <div className={'flex grid grid-cols-3 gap-5'}>
          <div className={'rounded-xl h-[100px] items-center flex'}>
            <StatsCard
              title={'Service Leads'}
              content={formatNumber(defaultConversions?.service_lead?.conversions || 0)}
              color={'bg-chrysler-blue/20'}
              border={false}
              rightSlot={<LeadsIcon />}
              hasAnimatedDescriptionOverflow={true}
              description={
                dealership?.enable_path_to_conversion_report
                  ? 'See the path that led to your leads'
                  : undefined
              }
              to={
                dealership?.enable_path_to_conversion_report
                  ? `${t('dealership.reports.conversion_path', {
                      dealership,
                    })}?${trackingTags
                      ?.filter((tag) =>
                        tag.conversion_tracking_tag_name?.includes('[SVL]'),
                      )
                      ?.map((tag) => `tag=${tag.conversion_tracking_tag_id}`)
                      ?.join('&')}`
                  : undefined
              }
            />
          </div>
          <div className={'rounded-xl h-[100px] items-center flex'}>
            <StatsCard
              title={'Request a Quote'}
              content={formatNumber(defaultConversions?.request_quote?.conversions || 0)}
              color={'bg-light-cyan'}
              border={false}
              rightSlot={<ClickToChatIcon />}
              hasAnimatedDescriptionOverflow={true}
              description={
                dealership?.enable_path_to_conversion_report
                  ? 'See the path that led to your calls'
                  : undefined
              }
              to={
                dealership?.enable_path_to_conversion_report
                  ? `${t('dealership.reports.conversion_path', {
                      dealership,
                    })}?${trackingTags
                      ?.filter((tag) =>
                        tag.conversion_tracking_tag_name?.includes('[RAQ]'),
                      )
                      ?.map((tag) => `tag=${tag.conversion_tracking_tag_id}`)
                      ?.join('&')}`
                  : undefined
              }
            />
          </div>
          <div className={'rounded-xl h-[100px] items-center flex'}>
            <StatsCard
              title={'Click to Call / Calls'}
              content={formatNumber(defaultConversions?.click_to_call?.conversions || 0)}
              color={'bg-champagne-pink'}
              border={false}
              rightSlot={<ClickToCallIcon />}
              hasAnimatedDescriptionOverflow={true}
              description={
                dealership?.enable_path_to_conversion_report
                  ? 'See the path that led to your chats'
                  : undefined
              }
              to={
                dealership?.enable_path_to_conversion_report
                  ? `${t('dealership.reports.conversion_path', {
                      dealership,
                    })}?${trackingTags
                      ?.filter((tag) =>
                        tag.conversion_tracking_tag_name?.includes('[CTC]'),
                      )
                      ?.map((tag) => `tag=${tag.conversion_tracking_tag_id}`)
                      ?.join('&')}`
                  : undefined
              }
            />
          </div>
        </div>
        <div className={'flex grid grid-cols-3 gap-5'}>
          <StatsCard
            title={conversions?.find((conversion) => conversion.id === 4)?.tag_name}
            content={formatNumber(
              Math.round(
                conversions?.find((conversion) => conversion.id === 4)?.conversions || 0,
              ),
            )}
            color={'bg-white'}
            border
          />
          <StatsCard
            title={'Find a Dealer Conversion Rate'}
            content={`${formatNumber(
              Math.round(
                totalImpressions > 0
                  ? (10000 * (defaultConversions?.find_dealer?.conversions || 0)) /
                      totalImpressions
                  : 0,
              ) / 100,
            )}%`}
            color={'bg-white'}
            border
          />
          <StatsCard
            title={'Conversion Rate'}
            info={`Based on ${Object.values(defaultConversions)
              ?.map((d) => d?.tag_name)
              ?.join(', ')}`}
            content={`${formatNumber(
              Math.round(
                totalImpressions > 0
                  ? (10000 *
                      (Object.values(defaultConversions)?.reduce(
                        (acc, cur) => acc + (cur?.conversions || 0),
                        0,
                      ) || 0)) /
                      totalImpressions
                  : 0,
              ) / 100,
            )}%`}
            color={'bg-white'}
            border
          />
        </div>
        {totalBudget?.title?.length > 0 && (
          <div className="grid grid-cols-3 gap-5">
            <StatsCard
              title={'CPA'}
              content={formatCurrency(
                (totalBudget?.data ?? 0) /
                  Object.values(defaultConversions)?.reduce(
                    (acc, cur) => acc + (cur?.conversions ?? 0),
                    0,
                  ),
              )}
              color={'bg-white'}
              border
              info={'Cost per lead, call, or chat'}
            />
            <StatsCard
              title={'CPC'}
              content={formatCurrency(
                (totalBudget?.data ?? 0) /
                  metrics?.reduce((acc, cur) => acc + cur.clicks, 0),
              )}
              color={'bg-white'}
              border
              info={'Cost per click'}
            />
            <StatsCard
              title={'Cost per Site Visitor'}
              content={formatCurrency((totalBudget?.data || 0) / totalSiteVisitors || 0)}
              color={'bg-white'}
              border
            />
          </div>
        )}
        <Toast
          variant="basic"
          className={'font-bold py-4'}
          text="Note: Values are based on last impression date, not conversion date"
        />
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex font-bold text-sm text-primary-xiketic justify-between items-center'
            }>
            <div className="flex flex-row gap-2 items-center">
              <span>YTD Find a Dealer Conversion Rate</span>
            </div>
          </div>
          <div className={'h-[270px] p-2'}>
            <LineChart
              data={
                chartData?.sort((a, b) =>
                  dayjs(a.date).isAfter(dayjs(b.date)) ? 1 : -1,
                ) ?? []
              }
              gradient={'blue-to-purple'}
              yKey={'Find a Dealer Conversion Rate'}
              isLoading={isLoadingMonthlyConversions || isLoadingMonthlyImpressions}
              customOptions={{
                plugins: {
                  legend: {
                    display: true,
                    position: 'bottom',
                  },
                },
              }}
            />
          </div>
        </div>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex font-bold text-sm text-primary-xiketic justify-between items-center'
            }>
            <div className="flex flex-row gap-2 items-center">
              <span>Find a Dealer Conversion Rate by Campaign</span>
            </div>
          </div>
          <div className="h-[270px] p-2">
            <ComboChart
              data={campaignBudgetChartData ?? []}
              isLoading={isLoadingCampaigns}
              isPreviousData={isPreviousCampaignsData}
              xKey={'campaign_name'}
              y1Key={'Budget'}
              y2Key={'Find a Dealer Rate'}
            />
          </div>
        </div>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex font-bold text-sm text-primary-xiketic justify-between items-center'
            }>
            <div className="flex flex-row gap-2 items-center">
              <span>Find a Dealer Conversion Rate by Ad Group</span>
            </div>
          </div>
          <div className="h-[270px] p-2">
            <StackedBarChartV2
              data={
                vendorCampaignConversions?.map((d) => ({
                  adGroup: d.category,
                  value: d['Find a Dealer Conversion Rate'],
                  campaign: d.campaign,
                })) ?? []
              }
              isPreviousData={isPreviousVendorCampaignsData}
              isLoading={isLoadingVendorCampaigns}
              groupKey={'campaign'}
              xKey={'adGroup'}
              yKey={'value'}
            />
          </div>
        </div>

        <div className="flex flex-col gap-8 pb-5">
          <div className="flex flex-col gap-3">
            <span className={'font-bold text-primary-xiketic text-md'}>Campaigns</span>
            <Table
              hidePagination={true}
              emptyLabel={'No campaigns found'}
              columns={columns}
              filters={{
                ...filters.dateRange,
                excludeProductCode: 'MOB',
                campaign_ids: campaignFilters,
              }}
              fetchFn={useGetCampaignsSummaryList}
            />
          </div>
        </div>
      </div>
    </AudienceTemplate>
  )
}

export default OmniConversionsReport
